import {
  generateInviteURL,
  isShareTargetPickerAvailable,
  openShareTargetPicker,
  sendInviteURL,
} from 'src/lib/sharing';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { makePayload } from 'src/lib/analytics';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { AnalyticsProperties } from '@play-co/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { getCreativeText } from 'src/creatives/text';
import { getRandomAddFriendsV2ShareCreative } from 'src/creatives/share';
import { track } from 'src/lib/analytics/utils';
import { getRandomAddFriendsV2Asset } from 'src/replicant/chatbot/flex/invite';
import {
  createFlexContainer,
  createOpenGameAction,
  FlexCreativeAsset,
} from 'src/replicant/chatbot/flex/utils';
import { AlternativeOpenWebBuckets } from 'src/replicant/utils/payload';
import { getBuruFriendsCount } from 'src/replicant/friends/friends.getters';
import { withLoading } from 'src/lib/utils';

/**
 * A/B 0468 V2: pet "want_to_add_friend" dialog completion path.
 * Public entry point invoked by PetConversationController when the user
 * resolves the wantToInvite popup. Shares directly via LINE Share Target
 * Picker (or native share fallback), skipping popupAddFriend. The invite
 * URL is generated lazily — only when the native fallback is taken — to
 * avoid wasted backend calls and to keep STP working when URL generation
 * is unavailable. Errors from the underlying share are swallowed so the
 * conversation flow (tutorial handling, idle sequence) is never broken
 * by a user-cancel of the share picker.
 */
export async function openAddFriendDirectShare(): Promise<void> {
  try {
    await shareAddFriendFromPetDialog(async () => {
      const urlData = await withLoading(async () =>
        generateInviteURL({
          creativeAsset: await getRandomAddFriendsV2ShareCreative(),
          creativeText: getCreativeText('invite'),
          data: {
            feature: Feature.PET_DIALOG._,
            $subFeature: Feature.PET_DIALOG.NATIVE_SHARE,
            originFeature: Feature.PET_DIALOG._,
            originSubFeature: Feature.PET_DIALOG.NATIVE_SHARE,
          },
        }),
      );
      return urlData?.inviteUrl ?? null;
    });
  } catch (err) {
    console.error('[wantToInvite] share failed', err);
  }
}

/**
 * Dispatcher between the STP and native-share sender branches.
 * Takes a lazy URL getter so the (potentially backend-hitting) invite-URL
 * generation only runs when the native fallback is actually taken — STP
 * builds its own flex payload and never uses the URL.
 */
async function shareAddFriendFromPetDialog(
  getInviteUrl: () => Promise<string | null>,
): Promise<void> {
  if (isShareTargetPickerAvailable()) {
    await sendAddFriendShareTargetPickerFromPetDialog();
    return;
  }

  const inviteUrl = await getInviteUrl();
  if (!inviteUrl) return;

  await sendNativeShareFromPetDialog(inviteUrl);
}

/**
 * Dedicated STP sender. Bakes PET_DIALOG feature/subFeature into the
 * flex payload so receiver-side events inherit pet_dialog attribution,
 * and emits the sender-side FriendRequestSend track here so all
 * pet_dialog STP attribution lives in one place.
 */
async function sendAddFriendShareTargetPickerFromPetDialog(): Promise<void> {
  const creativeAsset = getRandomAddFriendsV2Creative();
  const creativeText = getCreativeText('invite');
  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';

  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      channel: 'SHARE' as AnalyticsProperties.Channel,
      feature: Feature.PET_DIALOG._,
      subFeature: Feature.PET_DIALOG.LINE_SHARE,
      $subFeature: Feature.PET_DIALOG.LINE_SHARE,
      originFeature: Feature.PET_DIALOG._,
      originSubFeature: Feature.PET_DIALOG.LINE_SHARE,
      webReferrer: user().id,
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
    });

  const flexMessage = createAddFriendsV2FlexMessage({
    title,
    asset: creativeAsset,
    cta: creativeText.cta,
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  await openShareTargetPicker(flexMessage, analyticsEventProperties);

  track('FriendRequestSend', {
    method: 'line_share',
    friendCount: getBuruFriendsCount(user()),
    originFeature: Feature.PET_DIALOG._,
    originSubFeature: Feature.PET_DIALOG.LINE_SHARE,
  });
}

/**
 * Dedicated native-share sender. Mirrors the STP variant — emits both
 * SendInviteURL and FriendRequestSend with PET_DIALOG attribution so
 * all pet_dialog native share analytics live in one place.
 */
async function sendNativeShareFromPetDialog(inviteUrl: string): Promise<void> {
  const shareMethod = await sendInviteURL({
    inviteUrl,
    description: i18n('friends.addFriendPopup.shareDescription'),
  });

  track('SendInviteURL', {
    url: inviteUrl,
    shareMethod,
    feature: Feature.PET_DIALOG._,
  });

  track('FriendRequestSend', {
    method: 'native_share',
    friendCount: getBuruFriendsCount(user()),
    originFeature: Feature.PET_DIALOG._,
    originSubFeature: Feature.PET_DIALOG.NATIVE_SHARE,
  });
}

/**
 * Create Add Friends V2 flex message with hero image only.
 * File-private copy — keeps wantToInvite.share self-contained without
 * needing to export the equivalent helper from addFriendsV2.share.
 */
function createAddFriendsV2FlexMessage(args: {
  title: string;
  asset: FlexCreativeAsset;
  cta: string;
  payload: object & { alternativeWebIABBucket: AlternativeOpenWebBuckets };
}) {
  const { title, asset, cta, payload } = args;
  return createFlexContainer(title, {
    type: 'bubble',
    hero: {
      type: 'image',
      url: asset.image,
      size: 'full',
      aspectRatio: '73:41', // 730:410 creative dimensions
      aspectMode: 'cover',
      action: createOpenGameAction(cta, payload),
    },
  });
}

/**
 * Get random Add Friends V2 creative asset for share target picker.
 * File-private copy — see note on createAddFriendsV2FlexMessage.
 */
function getRandomAddFriendsV2Creative(): FlexCreativeAsset {
  const creative = getRandomAddFriendsV2Asset(Math.random);
  creative.image = StateObserver.replicant.getChatbotAssetUrl(creative.image);
  return creative;
}
